import { AllTrending } from "@/api/publicRequests/trending/getAllTrending";
import { TrendingSBC } from "@/api/publicRequests/trending/getTrendingSbcs";
import { ChipButton } from "@/design-system/Button/Button";
import {
  PlayerCardWithMetaRating,
  PlayerCardWithMetaRatingSkeleton,
} from "@/design-system/PlayerCard/PlayerCard";
import {
  FrontPageSubsection,
  trackExplore,
  trackViewContent,
} from "@/events/frontPageMixpanel";
import { cn } from "@/lib/utils";
import {
  NewContentType,
  activeNewTabSelector,
  activeTrendingTabSelector,
  fetchNewContent,
  fetchTrendingContent,
  frontPageSelector,
  setActiveNewTab,
  setActiveTrendingTab,
} from "@/redux/frontPage/frontPageSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { Player, isEvolution } from "@repo/shared/types";
import { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  ALL_GRID,
  CAROUSEL_CARD_SCALE,
  FrontPageContent,
  GRID_CARD_SCALE,
  SBC_GRID,
} from "../FrontPageContent";
import { ViewAllButton } from "../buttons/ViewAllButton";
import { FrontPageSbcSet, FrontPageSbcSetSkeleton } from "./FrontPageSbcSet";

export type TrendingContentType = "players" | "evolutions" | "sbc" | "all";

type ViewAllTarget = { path: string; section: string; subsection: string };

// The mixed "all" tab has nowhere to point, so no View All
const TRENDING_VIEW_ALL: Partial<Record<TrendingContentType, ViewAllTarget>> = {
  players: {
    path: "/players?sort=trending",
    section: "Players",
    subsection: "Overview",
  },
  evolutions: {
    path: "/evolutions/players",
    section: "Evolutions",
    subsection: "Players",
  },
  sbc: { path: "/sbc?sort=trending", section: "SBCs", subsection: "Overview" },
};

// The SBC page keeps its sort for the session, so pin newest
const NEW_VIEW_ALL: Partial<Record<NewContentType, ViewAllTarget>> = {
  players: { path: "/players", section: "Players", subsection: "Overview" },
  sbc: { path: "/sbc?sort=newest", section: "SBCs", subsection: "Overview" },
};

const FeedViewAll = ({
  subsection,
  target,
}: {
  subsection: FrontPageSubsection;
  target?: ViewAllTarget;
}) => {
  const navigate = useNavigate();
  if (!target) return null;
  return (
    <ViewAllButton
      onClick={() => {
        trackExplore({
          destination_section: target.section,
          destination_subsection: target.subsection,
          subsection,
        });
        navigate(target.path);
      }}
    />
  );
};

const playerKey = (player: { resourceId: number; evolutionData?: any }) =>
  player.resourceId + (player.evolutionData?.evolutionPath?.join("-") ?? "");

// Evo players open on the evolutions page, which loads the evolutions they need.
const playerLink = (player: Player) => ({
  basePath: isEvolution(player)
    ? ("/evolutions/players" as const)
    : ("/players" as const),
});

const allKey = (item: AllTrending, index: number) =>
  item.contentType === "sbc"
    ? `sbc-${(item.content as TrendingSBC).id}`
    : `player-${playerKey(item.content as Player)}-${index}`;

// Shared player-grid renderers used by both feeds. `onClick` fires view_content
// on navigation; the feed supplies it because only the feed knows its subsection.
const renderPlayerGridCard = (player: Player, onClick?: () => void) => (
  <PlayerCardWithMetaRating
    hideLabel
    fullWidth
    scale={GRID_CARD_SCALE}
    player={player}
    asLink={playerLink(player)}
    onClick={onClick}
  />
);
const renderPlayerCarouselCard = (player: Player, onClick?: () => void) => (
  <PlayerCardWithMetaRating
    scale={CAROUSEL_CARD_SCALE}
    player={player}
    asLink={playerLink(player)}
    onClick={onClick}
  />
);
const renderPlayerGridSkeleton = () => (
  <PlayerCardWithMetaRatingSkeleton
    hideLabel
    scale={GRID_CARD_SCALE}
    fullWidth
  />
);

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <span className="uppercase text-foreground text-h1">{children}</span>
);

/**
 * Trending + New as two stacked sections. Both feeds are self-contained and
 * fetch their own data on mount, so the order is a pure render concern. It was
 * A/B tested (feed-order test, retired 2026-08-12); Trending on top won and is
 * now the only order.
 */
export const TrendingOrNewContent = () => {
  const { t } = useTranslation("frontPage");
  return (
    <div className="flex flex-col gap-y-12 min-h-screen">
      <section className="flex flex-col gap-y-4">
        <SectionTitle>{t("sections.trending")}</SectionTitle>
        <div>
          <TrendingFeed />
        </div>
      </section>
      <section className="flex flex-col gap-y-4">
        <SectionTitle>{t("sections.new")}</SectionTitle>
        <div>
          <NewFeed />
        </div>
      </section>
    </div>
  );
};

const TrendingFeed = () => {
  const { t } = useTranslation("frontPage");
  const activeTab = useSelector(activeTrendingTabSelector);
  const dispatch = useDispatch<AppDispatch>();

  const {
    trendingPlayers,
    trendingEvoPlayers,
    trendingSbcs,
    allTrending,
    contentStatus,
  } = useSelector(frontPageSelector);

  const isSbcTab = activeTab === "sbc";
  const isAllTab = activeTab === "all";

  useEffect(() => {
    dispatch(fetchTrendingContent({ type: activeTab }));
  }, [dispatch, activeTab]);

  const players =
    activeTab === "players"
      ? trendingPlayers
      : activeTab === "evolutions"
        ? trendingEvoPlayers
        : [];

  const trackPlayer = (player: Player) =>
    trackViewContent({
      subsection: "Trending",
      content_type: activeTab === "evolutions" ? "evo_player" : "player",
      new_content: player.newPlayer,
    });

  // The "all" feed is mixed, so the content type comes from the item itself.
  const trackAllItem = (item: AllTrending) => {
    if (item.contentType === "sbc") {
      trackViewContent({ subsection: "Trending", content_type: "sbc" });
      return;
    }
    const player = item.content as Player;
    trackViewContent({
      subsection: "Trending",
      content_type:
        item.contentType === "evolutionPlayer" ? "evo_player" : "player",
      new_content: player.newPlayer,
    });
  };

  const trackSbc = () =>
    trackViewContent({ subsection: "Trending", content_type: "sbc" });

  const contentPicker = (
    <div className="flex w-full flex-row justify-between gap-x-2">
      <div className="flex flex-row gap-x-2">
        <ChipButton
          selected={activeTab === "all"}
          onClick={() => dispatch(setActiveTrendingTab("all"))}
          className={cn(
            "data-[selected=true]:text-neutral-100",
            "data-[selected=true]:bg-neutral-700",
            "data-[selected=true]:border-neutral-600",
          )}
        >
          {t("tabs.all")}
        </ChipButton>
        <ChipButton
          selected={activeTab === "players"}
          onClick={() => dispatch(setActiveTrendingTab("players"))}
          className={cn(
            "data-[selected=true]:text-amber-400",
            "data-[selected=true]:bg-amber-900/50",
            "data-[selected=true]:border-amber-900",
          )}
        >
          {t("tabs.players")}
        </ChipButton>
        <ChipButton
          selected={activeTab === "evolutions"}
          onClick={() => dispatch(setActiveTrendingTab("evolutions"))}
          className={cn(
            "data-[selected=true]:text-emerald-400",
            "data-[selected=true]:bg-emerald-900/50",
            "data-[selected=true]:border-emerald-900",
          )}
        >
          {t("tabs.evolutionPlayers")}
        </ChipButton>
        <ChipButton
          selected={activeTab === "sbc"}
          onClick={() => dispatch(setActiveTrendingTab("sbc"))}
          className={cn(
            "data-[selected=true]:text-indigo-400",
            "data-[selected=true]:bg-indigo-900/50",
            "data-[selected=true]:border-indigo-900",
          )}
        >
          {t("tabs.sbcs")}
        </ChipButton>
      </div>
      <FeedViewAll
        subsection="Trending"
        target={TRENDING_VIEW_ALL[activeTab]}
      />
    </div>
  );

  if (isSbcTab) {
    return (
      <FrontPageContent
        key={activeTab}
        header={contentPicker}
        status={contentStatus}
        items={trendingSbcs}
        limitToVisible
        getKey={(sbc) => sbc.id}
        grid={SBC_GRID}
        renderGridCard={(sbc) => (
          <FrontPageSbcSet sbc={sbc} className="w-full" onClick={trackSbc} />
        )}
        renderCarouselCard={(sbc) => (
          <FrontPageSbcSet sbc={sbc} className="w-80" onClick={trackSbc} />
        )}
        renderGridSkeleton={() => (
          <FrontPageSbcSetSkeleton className="w-full" />
        )}
        renderCarouselSkeleton={() => (
          <FrontPageSbcSetSkeleton className="w-80" />
        )}
      />
    );
  }

  if (isAllTab) {
    return (
      <FrontPageContent
        key={activeTab}
        header={contentPicker}
        status={contentStatus}
        items={allTrending}
        limitToVisible
        getItemSpan={(item) => (item.contentType === "sbc" ? 2 : 1)}
        getKey={allKey}
        grid={ALL_GRID}
        renderGridCard={(item) =>
          item.contentType === "sbc" ? (
            <FrontPageSbcSet
              sbc={item.content as TrendingSBC}
              className="col-span-2 w-full"
              onClick={() => trackAllItem(item)}
            />
          ) : (
            <PlayerCardWithMetaRating
              fullWidth
              scale={GRID_CARD_SCALE}
              player={item.content as Player}
              asLink={playerLink(item.content as Player)}
              onClick={() => trackAllItem(item)}
            />
          )
        }
        renderCarouselCard={(item) =>
          item.contentType === "sbc" ? (
            <FrontPageSbcSet
              sbc={item.content as TrendingSBC}
              className="w-80"
              onClick={() => trackAllItem(item)}
            />
          ) : (
            <PlayerCardWithMetaRating
              scale={CAROUSEL_CARD_SCALE}
              player={item.content as Player}
              asLink={playerLink(item.content as Player)}
              onClick={() => trackAllItem(item)}
            />
          )
        }
        renderGridSkeleton={() => (
          <PlayerCardWithMetaRatingSkeleton scale={GRID_CARD_SCALE} fullWidth />
        )}
      />
    );
  }

  return (
    <FrontPageContent
      key={activeTab}
      header={contentPicker}
      status={contentStatus}
      items={players}
      limitToVisible
      getKey={playerKey}
      renderGridCard={(player) =>
        renderPlayerGridCard(player, () => trackPlayer(player))
      }
      renderCarouselCard={(player) =>
        renderPlayerCarouselCard(player, () => trackPlayer(player))
      }
      renderGridSkeleton={renderPlayerGridSkeleton}
    />
  );
};

const NEW_TABS: {
  value: NewContentType;
  labelKey: "tabs.players" | "tabs.sbcs";
  selectedClass: string;
}[] = [
  {
    value: "players",
    labelKey: "tabs.players",
    selectedClass: cn(
      "data-[selected=true]:text-amber-400",
      "data-[selected=true]:bg-amber-900/50",
      "data-[selected=true]:border-amber-900",
    ),
  },
  // {
  //   value: "evolutions",
  //   labelKey: "tabs.evolutionPlayers",
  //   selectedClass: cn(
  //     "data-[selected=true]:text-emerald-400",
  //     "data-[selected=true]:bg-emerald-900/50",
  //     "data-[selected=true]:border-emerald-900",
  //   ),
  // },
  {
    value: "sbc",
    labelKey: "tabs.sbcs",
    selectedClass: cn(
      "data-[selected=true]:text-indigo-400",
      "data-[selected=true]:bg-indigo-900/50",
      "data-[selected=true]:border-indigo-900",
    ),
  },
];

const NewFeed = () => {
  const { t } = useTranslation("frontPage");
  const activeTab = useSelector(activeNewTabSelector);
  const dispatch = useDispatch<AppDispatch>();
  const { newPlayers, newEvoPlayers, newSbcs, newContentStatus } =
    useSelector(frontPageSelector);
  useEffect(() => {
    dispatch(fetchNewContent({ type: activeTab }));
  }, [dispatch, activeTab]);

  const trackPlayer = (player: Player) =>
    trackViewContent({
      subsection: "New",
      content_type: activeTab === "evolutions" ? "evo_player" : "player",
      new_content: player.newPlayer,
    });

  const trackSbc = () =>
    trackViewContent({ subsection: "New", content_type: "sbc" });

  const contentPicker = (
    <div className="flex flex-row gap-x-2">
      {NEW_TABS.map((tab) => (
        <ChipButton
          key={tab.value}
          selected={activeTab === tab.value}
          onClick={() => dispatch(setActiveNewTab(tab.value))}
          className={tab.selectedClass}
        >
          {t(tab.labelKey)}
        </ChipButton>
      ))}
      <div className="ml-auto">
        <FeedViewAll subsection="New" target={NEW_VIEW_ALL[activeTab]} />
      </div>
    </div>
  );

  if (activeTab === "sbc") {
    return (
      <FrontPageContent
        key={activeTab}
        header={contentPicker}
        status={newContentStatus}
        items={newSbcs}
        limitToVisible
        getKey={(sbc) => sbc.id}
        grid={SBC_GRID}
        renderGridCard={(sbc) => (
          <FrontPageSbcSet sbc={sbc} className="w-full" onClick={trackSbc} />
        )}
        renderCarouselCard={(sbc) => (
          <FrontPageSbcSet sbc={sbc} className="w-80" onClick={trackSbc} />
        )}
        renderGridSkeleton={() => (
          <FrontPageSbcSetSkeleton className="w-full" />
        )}
        renderCarouselSkeleton={() => (
          <FrontPageSbcSetSkeleton className="w-80" />
        )}
      />
    );
  }

  return (
    <FrontPageContent
      key={activeTab}
      header={contentPicker}
      status={newContentStatus}
      items={activeTab === "players" ? newPlayers : newEvoPlayers}
      limitToVisible
      getKey={playerKey}
      renderGridCard={(player) =>
        renderPlayerGridCard(player, () => trackPlayer(player))
      }
      renderCarouselCard={(player) =>
        renderPlayerCarouselCard(player, () => trackPlayer(player))
      }
      renderGridSkeleton={renderPlayerGridSkeleton}
    />
  );
};
